import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, onSnapshot, deleteDoc } from 'firebase/firestore';

const AdminSupportTickets = () => {
  const { currentUser } = useAuth();
  const { createNotification } = useNotifications();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagesListener, setMessagesListener] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [activeTab, setActiveTab] = useState('ALL');
  const [globalListener, setGlobalListener] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // AI Enhancement states
  const [enhancingMessage, setEnhancingMessage] = useState(false);
  const [enhancedMessage, setEnhancedMessage] = useState('');
  const [showEnhancedMessage, setShowEnhancedMessage] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0
  });

  useEffect(() => {
    loadTickets();
    setupGlobalListener();
    
    return () => {
      if (globalListener) {
        globalListener();
      }
      if (messagesListener) {
        messagesListener();
      }
    };
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    calculateStats();
    filterTickets();
  }, [tickets, activeTab]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const setupGlobalListener = () => {
    const ticketsRef = collection(db, 'tickets');
    const unsubscribe = onSnapshot(ticketsRef, (snapshot) => {
      const ticketsList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        ticketsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          createdDate: data.createdAt?.toDate ? 
            data.createdAt.toDate().toISOString().split('T')[0] : 
            (data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
        });
      });
      
      ticketsList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setTickets(ticketsList);
      setLoading(false);
    });
    
    setGlobalListener(() => unsubscribe);
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const ticketsRef = collection(db, 'tickets');
      let querySnapshot;
      
      try {
        const q = query(ticketsRef, orderBy('createdAt', 'desc'));
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        console.warn('OrderBy failed, trying simple query:', orderError);
        querySnapshot = await getDocs(ticketsRef);
      }
      
      const ticketsList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        ticketsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          createdDate: data.createdAt?.toDate ? 
            data.createdAt.toDate().toISOString().split('T')[0] : 
            (data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
        });
      });
      
      // Sort manually by creation date (newest first)
      ticketsList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      console.log('Admin loaded tickets:', ticketsList.length);
      setTickets(ticketsList);
      
    } catch (error) {
      console.error('Error loading tickets:', error);
      // Handle different error types gracefully
      if (error.code === 'permission-denied') {
        showNotification('Permission denied. Please check your admin access.', 'error');
      } else if (error.code === 'unavailable') {
        showNotification('Service temporarily unavailable. Please try again later.', 'error');
      } else {
        console.warn('Tickets collection might not exist yet, starting with empty list');
        setTickets([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = tickets.length;
    const open = tickets.filter(ticket => ticket.status === 'open').length;
    const inProgress = tickets.filter(ticket => ticket.status === 'in-progress').length;
    const resolved = tickets.filter(ticket => ticket.status === 'resolved').length;
    const closed = tickets.filter(ticket => ticket.status === 'closed').length;
    
    setStats({ total, open, inProgress, resolved, closed });
  };

  const filterTickets = () => {
    let filtered = tickets;
    
    switch (activeTab) {
      case 'OPEN':
        filtered = tickets.filter(ticket => ticket.status === 'open');
        break;
      case 'IN PROGRESS':
        filtered = tickets.filter(ticket => ticket.status === 'in-progress');
        break;
      case 'RESOLVED':
        filtered = tickets.filter(ticket => ticket.status === 'resolved');
        break;
      case 'CLOSED':
        filtered = tickets.filter(ticket => ticket.status === 'closed');
        break;
      default:
        filtered = tickets;
    }
    
    setFilteredTickets(filtered);
  };

  const setupMessagesListener = (ticketId) => {
    if (messagesListener) {
      messagesListener();
    }
    
    const ticketRef = doc(db, 'tickets', ticketId);
    const unsubscribe = onSnapshot(ticketRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setSelectedTicket(prev => ({
          ...prev,
          ...data,
          id: doc.id
        }));
      }
    });
    
    setMessagesListener(() => unsubscribe);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setStatusUpdate(ticket.status || 'open');
    setShowModal(true);
    setupMessagesListener(ticket.id);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
    setNewMessage('');
    setStatusUpdate('');
    setEnhancedMessage('');
    setShowEnhancedMessage(false);
    
    if (messagesListener) {
      messagesListener();
      setMessagesListener(null);
    }
  };

  const enhanceMessageWithAI = async () => {
    if (!newMessage.trim()) {
      showNotification('Please enter a message to correct.', 'error');
      return;
    }

    setEnhancingMessage(true);
    try {
      // Import translation service dynamically
      const { translateAndCorrect } = await import('../../utils/translationService');
      
      // Translate and correct grammar using free services
      // 1. Detects language (Spanish/English)
      // 2. Translates to English if needed (LibreTranslate/MyMemory/Google)
      // 3. Corrects grammar (LanguageTool)
      const result = await translateAndCorrect(newMessage.trim());

      if (!result.success) {
        throw new Error(result.error || 'Enhancement failed');
      }

      let enhanced = result.processedText;
      
      // Remove any quotation marks that might be added by AI
      if (enhanced) {
        enhanced = enhanced.replace(/^["']|["']$/g, '');
        setEnhancedMessage(enhanced);
        setShowEnhancedMessage(true);
        showNotification('Message corrected successfully! Review and use if satisfied.', 'success');
      } else {
        throw new Error('No enhanced content received');
      }
    } catch (error) {
      console.error('Error enhancing message:', error);
      showNotification('Failed to correct message. Please try again or send the original message.', 'error');
    } finally {
      setEnhancingMessage(false);
    }
  };

  const useEnhancedMessage = () => {
    setNewMessage(enhancedMessage);
    setShowEnhancedMessage(false);
    setEnhancedMessage('');
    showNotification('Corrected message applied! You can edit further if needed.', 'success');
  };

  const discardEnhancedMessage = () => {
    setShowEnhancedMessage(false);
    setEnhancedMessage('');
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const message = {
        id: Date.now().toString(),
        text: newMessage.trim(),
        sender: 'admin',
        senderName: currentUser.displayName || 'Support Team',
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: false
      };
      
      const currentMessages = selectedTicket.messages || [];
      const updatedMessages = [...currentMessages, message];
      
      await updateDoc(doc(db, 'tickets', selectedTicket.id), {
        messages: updatedMessages,
        updatedAt: new Date(),
        hasUnreadMessages: false
      });
      
      // Notify client about response
      try {
        await createNotification({
          userId: selectedTicket.userId,
          title: 'Support Team Reply',
          message: `Support team replied to your ticket ${selectedTicket.ticketId}: ${selectedTicket.subject}`,
          icon: '🎫',
          category: 'support',
          priority: 'medium',
          relatedId: selectedTicket.id,
          relatedType: 'ticket',
          actionUrl: '/support'
        });
      } catch (notificationError) {
        console.error('Error sending notification to client:', notificationError);
      }
      
      setNewMessage('');
      showNotification('Message sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message. Please try again.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const updateTicketStatus = async () => {
    if (!selectedTicket || !statusUpdate) return;
    
    try {
      await updateDoc(doc(db, 'tickets', selectedTicket.id), {
        status: statusUpdate,
        updatedAt: new Date()
      });
      
      // Notify client about status change
      try {
        const statusMessages = {
          'open': 'Your support ticket has been reopened',
          'in-progress': 'Your support ticket is now being worked on',
          'resolved': 'Your support ticket has been resolved',
          'closed': 'Your support ticket has been closed'
        };
        
        await createNotification({
          userId: selectedTicket.userId,
          title: 'Ticket Status Updated',
          message: `${statusMessages[statusUpdate]} - ${selectedTicket.ticketId}: ${selectedTicket.subject}`,
          icon: '🎫',
          category: 'support',
          priority: 'medium',
          relatedId: selectedTicket.id,
          relatedType: 'ticket',
          actionUrl: '/support'
        });
      } catch (notificationError) {
        console.error('Error sending status notification to client:', notificationError);
      }
      
      showNotification('Ticket status updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error updating ticket status:', error);
      showNotification('Failed to update ticket status.', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#ff9800';
      case 'in-progress': return '#4caf50';
      case 'resolved': return '#4caf50';
      case 'closed': return '#666';
      default: return '#666';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#666';
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = 
      status === 'open' ? 'status-warning-modern' :
      status === 'in-progress' ? 'status-info-modern' :
      status === 'resolved' ? 'status-success-modern' :
      status === 'closed' ? 'status-pending-modern' :
      'status-pending-modern';

    const statusText = 
      status === 'in-progress' ? 'In Progress' :
      status.charAt(0).toUpperCase() + status.slice(1);
    
    return (
      <span className={`status-badge-modern ${statusClass}`}>
        {statusText}
      </span>
    );
  };

  const getMessageCount = (ticket) => {
    return ticket.messages ? ticket.messages.length : 0;
  };

  const deleteTicket = async (ticketId) => {
    try {
      const confirmDelete = window.confirm('Are you sure you want to delete this support ticket? This action cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'tickets', ticketId));
      const updated = tickets.filter(t => t.id !== ticketId);
      setTickets(updated);
      setNotification({ message: 'Ticket deleted successfully.', type: 'success' });
    } catch (error) {
      console.error('Error deleting ticket:', error);
      setNotification({ message: 'Failed to delete ticket.', type: 'error' });
    }
  };

  return (
    <Layout>
      <div className="analytics-container admin-support-page" style={{ 
        padding: windowWidth < 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box',
        height: 'auto',
        minHeight: 'auto'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: windowWidth < 768 ? '0.5rem' : '20px',
            left: windowWidth < 768 ? '0.5rem' : 'auto',
            zIndex: 1000,
            maxWidth: windowWidth < 768 ? 'none' : '400px',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500',
            wordWrap: 'break-word'
          }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '50%',
            width: windowWidth < 480 ? '32px' : '40px',
            height: windowWidth < 480 ? '32px' : '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: windowWidth < 480 ? '0.75rem' : '1rem',
            flexShrink: 0
          }}>
            <img 
              src="/supporticket.png" 
              alt="Support Tickets" 
              style={{ 
                width: windowWidth < 480 ? '20px' : '28px', 
                height: windowWidth < 480 ? '20px' : '28px', 
                objectFit: 'contain'
              }} 
            />
          </div>
          <h1 style={{ 
            fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
            fontWeight: 'bold', 
            color: '#333', 
            margin: 0,
            wordBreak: 'break-word',
            lineHeight: 1.2
          }}>
            Manage Support Tickets
          </h1>
        </div>

        {/* Statistics Cards - HARDCODED ALL 5 CARDS */}
        <div className="stats-cards-container" style={{ 
          display: windowWidth <= 480 ? 'flex' : 'grid',
          flexDirection: windowWidth <= 480 ? 'column' : 'row',
          gridTemplateColumns: windowWidth <= 480 ? '1fr' : 
                               windowWidth <= 768 ? '1fr 1fr' : 
                               windowWidth <= 1024 ? '1fr 1fr 1fr' : 
                               '1fr 1fr 1fr 1fr 1fr', 
          gap: '0.75rem',
          marginBottom: '2rem',
          padding: '1rem',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'visible',
          boxSizing: 'border-box',
          minHeight: windowWidth <= 480 ? '400px' : 'auto',
          height: 'auto'
        }}>
          {/* CARD 1: Total Tickets */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Total Tickets
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.total || 0}
            </div>
          </div>

          {/* CARD 2: Open Tickets */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Open Tickets
            </div>
            <div style={{ color: '#ff9800', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.open || 0}
            </div>
          </div>

          {/* CARD 3: In Progress */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              In Progress
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.inProgress || 0}
            </div>
          </div>

          {/* CARD 4: Resolved */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Resolved
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.resolved || 0}
            </div>
          </div>

          {/* CARD 5: Closed */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Closed
            </div>
            <div style={{ color: '#666', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.closed || 0}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          maxWidth: '100%'
        }}>
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #eee',
            backgroundColor: '#f8f9fa',
            overflowX: windowWidth < 768 ? 'auto' : 'visible',
            scrollbarWidth: 'thin',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%'
          }}>
            {[
              { key: 'ALL', label: 'ALL', count: stats.total },
              { key: 'OPEN', label: 'OPEN', count: stats.open },
              { key: 'IN PROGRESS', label: 'IN PROGRESS', count: stats.inProgress },
              { key: 'RESOLVED', label: 'RESOLVED', count: stats.resolved },
              { key: 'CLOSED', label: 'CLOSED', count: stats.closed }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: windowWidth <= 480 ? '0.5rem 0.75rem' : windowWidth <= 768 ? '0.75rem 1rem' : '1rem 1.5rem',
                  border: 'none',
                  backgroundColor: activeTab === tab.key ? '#4caf50' : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: windowWidth <= 480 ? '0.7rem' : windowWidth <= 768 ? '0.8rem' : '0.875rem',
                  fontWeight: '600',
                  borderBottom: activeTab === tab.key ? '2px solid #4caf50' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minWidth: 'fit-content',
                  transition: 'all 0.2s ease'
                }}
              >
                {windowWidth <= 480 ? 
                  `${tab.label} (${tab.count})` : 
                  `${tab.label} (${tab.count})`
                }
              </button>
            ))}
          </div>

          {/* Table */}
          <div style={{ 
            padding: windowWidth < 480 ? '0.75rem' : '1rem',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                <p>Loading support tickets...</p>
              </div>
            ) : (
              <>
                {windowWidth >= 768 ? (
                  <>
                    {/* Desktop Table Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                      gap: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#666'
                    }}>
                      <div>Customer</div>
                      <div>Subject</div>
                      <div>Category</div>
                      <div>Priority</div>
                      <div>Status</div>
                      <div>Created</div>
                    </div>

                    {/* Desktop Table Rows */
                    }
                    {filteredTickets.length > 0 ? (
                      filteredTickets.map((ticket) => (
                        <div
                          key={ticket.id}
                          onClick={() => handleViewTicket(ticket)}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                            gap: '1rem',
                            padding: '1rem',
                            borderBottom: '1px solid #eee',
                            alignItems: 'center',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                              {ticket.userName}
                            </div>
                            <div style={{ color: '#666', fontSize: '0.8rem' }}>
                              {ticket.userEmail}
                            </div>
                            <div style={{ color: '#666', fontSize: '0.8rem' }}>
                              {ticket.ticketId}
                            </div>
                          </div>
                          
                          <div style={{ fontWeight: '500' }}>
                            {ticket.subject.length > 30 ? ticket.subject.substring(0, 30) + '...' : ticket.subject}
                          </div>
                          
                          <div style={{ textTransform: 'capitalize', color: '#666' }}>
                            {ticket.category}
                          </div>
                          
                          <div>
                            <span style={{
                              color: getPriorityColor(ticket.priority),
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            }}>
                              {ticket.priority}
                            </span>
                          </div>
                          
                          <div>
                            {getStatusBadge(ticket.status)}
                          </div>
                          
                          <div style={{ color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                            <span>{ticket.createdDate}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteTicket(ticket.id); }}
                              style={{
                                background: 'none',
                                color: 'inherit',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎫</div>
                        <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>No tickets found</h4>
                        <p>No support tickets match the current filter.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Mobile Card Layout */}
                    {filteredTickets.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {filteredTickets.map((ticket) => (
                          <div
                            key={ticket.id}
                            onClick={() => handleViewTicket(ticket)}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '1rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            {/* Mobile Card Header */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start',
                              marginBottom: '0.75rem',
                              padding: '1rem'
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontWeight: '600', 
                                  fontSize: '0.9rem',
                                  marginBottom: '0.25rem',
                                  wordBreak: 'break-word'
                                }}>
                                  {ticket.userName}
                                </div>
                                <div style={{ 
                                  color: '#666', 
                                  fontSize: '0.75rem',
                                  wordBreak: 'break-word'
                                }}>
                                  {ticket.userEmail}
                                </div>
                              </div>
                              <div style={{ flexShrink: 0, marginLeft: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {getStatusBadge(ticket.status)}
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteTicket(ticket.id); }}
                                  style={{
                                    background: 'none',
                                    color: 'inherit',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: '0.35rem 0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>

                            {/* Mobile Card Content */}
                            <div style={{ marginBottom: '0.75rem', padding: '1rem' }}>
                              <div style={{ 
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                marginBottom: '0.5rem',
                                lineHeight: 1.3,
                                wordBreak: 'break-word'
                              }}>
                                {ticket.subject}
                              </div>
                              
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap',
                                gap: '0.75rem',
                                fontSize: '0.75rem',
                                color: '#666'
                              }}>
                                <div>
                                  <strong>Category:</strong> <span style={{ textTransform: 'capitalize' }}>{ticket.category}</span>
                                </div>
                                <div>
                                  <strong>Priority:</strong> 
                                  <span style={{
                                    color: getPriorityColor(ticket.priority),
                                    fontWeight: '600',
                                    textTransform: 'capitalize',
                                    marginLeft: '0.25rem'
                                  }}>
                                    {ticket.priority}
                                  </span>
                                </div>
                                <div>
                                  <strong>Created:</strong> {ticket.createdDate}
                                </div>
                              </div>
                            </div>

                            {/* Mobile Card Footer */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingTop: '0.75rem',
                              borderTop: '1px solid #e5e7eb',
                              fontSize: '0.75rem',
                              color: '#666'
                            }}>
                              <div>ID: {ticket.ticketId}</div>
                              <div style={{ 
                                color: '#4caf50',
                                fontWeight: '500'
                              }}>
                                Tap to view →
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎫</div>
                        <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>No tickets found</h4>
                        <p>No support tickets match the current filter.</p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Ticket Management Modal */}
        {showModal && selectedTicket && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: windowWidth < 768 ? '1rem 0.5rem' : '2rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: windowWidth < 768 ? '8px' : '12px',
              padding: '0',
              maxWidth: windowWidth < 768 ? '100%' : '1000px',
              width: windowWidth < 768 ? '100%' : '95%',
              maxHeight: windowWidth < 768 ? 'calc(100vh - 2rem)' : '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              margin: windowWidth < 768 ? '0' : 'auto'
            }}>
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: windowWidth < 768 ? '1rem' : '1.5rem 2rem',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  color: '#2e7d32', 
                  fontSize: windowWidth < 480 ? '1.1rem' : windowWidth < 768 ? '1.25rem' : '1.5rem', 
                  fontWeight: '600',
                  wordBreak: 'break-word',
                  flex: 1,
                  marginRight: '1rem'
                }}>
                  Ticket Details - {selectedTicket.ticketId}
                </h2>
                <button 
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: windowWidth < 768 ? '1.25rem' : '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem',
                    flexShrink: 0
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ 
                display: windowWidth < 768 ? 'flex' : 'grid', 
                flexDirection: windowWidth < 768 ? 'column' : 'row',
                gridTemplateColumns: windowWidth >= 768 ? '1fr 1fr' : 'none', 
                gap: 0, 
                minHeight: windowWidth < 768 ? 'auto' : '600px'
              }}>
                {/* Left Column - Ticket Details */}
                <div style={{ 
                  padding: windowWidth < 768 ? '1rem' : '2rem', 
                  borderRight: windowWidth < 768 ? 'none' : '1px solid #eee',
                  borderBottom: windowWidth < 768 ? '1px solid #eee' : 'none'
                }}>
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>
                      Customer Information
                    </h3>
                    
                    <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '1.5rem' }}>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Name:</strong> {selectedTicket.userName}
                      </div>
                      <div>
                        <strong>Email:</strong> {selectedTicket.userEmail}
                      </div>
                    </div>

                    <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>
                      Ticket Information
                    </h3>
                    
                    <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '1.5rem' }}>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Subject:</strong> {selectedTicket.subject}
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Category:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedTicket.category}</span>
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Priority:</strong> 
                        <span style={{ 
                          color: getPriorityColor(selectedTicket.priority),
                          fontWeight: '600',
                          textTransform: 'capitalize',
                          marginLeft: '0.5rem'
                        }}>
                          {selectedTicket.priority}
                        </span>
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Status:</strong> 
                        <span style={{ marginLeft: '0.5rem' }}>
                          {getStatusBadge(selectedTicket.status)}
                        </span>
                      </div>
                      <div>
                        <strong>Created:</strong> {selectedTicket.createdDate}
                      </div>
                    </div>

                    <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>
                      Initial Description
                    </h3>
                    
                    <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '1.5rem' }}>
                      <div style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                        {selectedTicket.description}
                      </div>
                    </div>

                    {/* Update Status */}
                    <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>
                      Update Status
                    </h3>
                    
                    <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                      <select
                        value={statusUpdate}
                        onChange={(e) => setStatusUpdate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          marginBottom: '1rem'
                        }}
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                      
                      <button
                        onClick={updateTicketStatus}
                        style={{
                          background: '#2e7d32',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          width: '100%'
                        }}
                      >
                        Update Status
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column - Communication */}
                <div style={{ 
                  padding: windowWidth < 768 ? '1rem' : '2rem', 
                  display: 'flex', 
                  flexDirection: 'column'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>
                    💬 Conversation Thread
                  </h3>
                  
                  {/* Messages Display */}
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1rem',
                    flex: 1,
                    overflowY: 'auto',
                    marginBottom: '1rem',
                    border: '1px solid #e9ecef',
                    minHeight: '300px',
                    maxHeight: '400px'
                  }}>
                    {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {selectedTicket.messages.map((message, index) => (
                          <div
                            key={message.id || index}
                            style={{
                              display: 'flex',
                              justifyContent: message.sender === 'admin' ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <div
                              style={{
                                maxWidth: '80%',
                                padding: '0.75rem 1rem',
                                borderRadius: message.sender === 'admin' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                backgroundColor: message.sender === 'admin' ? '#4caf50' : '#fff',
                                color: message.sender === 'admin' ? 'white' : '#333',
                                border: message.sender === 'client' ? '1px solid #ddd' : 'none',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}
                            >
                              <div style={{ fontSize: '0.875rem', lineHeight: '1.4', marginBottom: '0.25rem' }}>
                                {message.text}
                              </div>
                              <div style={{
                                fontSize: '0.7rem',
                                opacity: 0.8,
                                textAlign: message.sender === 'admin' ? 'right' : 'left'
                              }}>
                                {message.sender === 'admin' ? (message.senderName || 'Support Team') : selectedTicket.userName}
                                <br />
                                {message.timestamp?.toDate ? 
                                  message.timestamp.toDate().toLocaleString() :
                                  new Date(message.timestamp).toLocaleString()
                                }
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '0.875rem',
                        padding: '2rem',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}
                  </div>

                  {/* Admin Reply Section */}
                  {selectedTicket.status !== 'closed' && (
                    <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', padding: '1rem' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '0.9rem', fontWeight: '600' }}>
                        Support Team Reply
                      </h4>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <textarea
                          placeholder="Type your response to the customer..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          rows="4"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            resize: 'vertical',
                            fontFamily: 'inherit'
                          }}
                        />
                      </div>

                      {/* AI Enhancement Section */}
                      {showEnhancedMessage && (
                        <div style={{
                          background: '#f0f9ff',
                          border: '1px solid #0ea5e9',
                          borderRadius: '8px',
                          padding: '1rem',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '1.25rem' }}>✨</span>
                            <h5 style={{ margin: 0, color: '#0369a1', fontSize: '0.875rem', fontWeight: '600' }}>
                              AI Corrected Response
                            </h5>
                          </div>
                          
                          <div style={{
                            background: 'white',
                            border: '1px solid #e0f2fe',
                            borderRadius: '6px',
                            padding: '0.75rem',
                            marginBottom: '1rem',
                            fontSize: '0.875rem',
                            lineHeight: '1.5',
                            color: '#374151'
                          }}>
                            {enhancedMessage}
                          </div>
                          
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={useEnhancedMessage}
                              style={{
                                background: '#0ea5e9',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}
                            >
                              ✅ Use This
                            </button>
                            <button
                              onClick={discardEnhancedMessage}
                              style={{
                                background: '#f3f4f6',
                                color: '#6b7280',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}
                            >
                              ❌ Discard
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={enhanceMessageWithAI}
                          disabled={enhancingMessage || !newMessage.trim()}
                          style={{
                            background: enhancingMessage || !newMessage.trim() ? '#f3f4f6' : '#8b5cf6',
                            color: enhancingMessage || !newMessage.trim() ? '#9ca3af' : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.75rem 1rem',
                            cursor: enhancingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            flex: '0 0 auto'
                          }}
                        >
                          {enhancingMessage ? '✨ Correcting...' : '✨ Correct with AI'}
                        </button>

                        <button
                          onClick={sendMessage}
                          disabled={sendingMessage || !newMessage.trim()}
                          style={{
                            background: sendingMessage || !newMessage.trim() ? '#ccc' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.75rem 1.5rem',
                            cursor: sendingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            flex: 1
                          }}
                        >
                          {sendingMessage ? '📤 Sending...' : '📤 Send Reply'}
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedTicket.status === 'closed' && (
                    <div style={{
                      backgroundColor: '#f5f5f5',
                      padding: '1rem',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '0.875rem'
                    }}>
                      🔒 This ticket has been closed. No new messages can be sent.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminSupportTickets;
